<template>
  <div>
    <h1 class="font-weight-thin">UAT Dashboard: Member Organization {{ serviceArea }}</h1>
    <v-container v-if="selectedPackId" fluid>
      <v-row>
        <v-col style="width: 100%">
          <progress-bar></progress-bar>
        </v-col>
      </v-row>
      <p data-cy="uatDashboard__progressBar--explanation">
        Note: UAT Progression Calculation, Not Started, and Unassigned counts do not include release notes with an
        Operationalize Type of "FYI", "Good to Know", or "Optional."
      </p>
      <v-row cols="12">
        <v-col sm="12">
          <v-row>
            <v-card class="pa-2 mr-2 elevation-0" color="transparent">
              <span class="subtitle-1"> <v-icon class="mr-1">assistant</v-icon>Pack Status: </span>
              <span class="subtitle-1">{{ pack.status }}</span>
            </v-card>
            <v-card class="pa-2 mr-2 elevation-0" color="transparent">
              <span class="subtitle-1"> <v-icon class="mr-1">date_range</v-icon>UAT Start: </span>
              <span class="subtitle-1" data-cy="uatDashboard__packStatus--uatStart">{{
                formatUatDate(pack.uatStart)
              }}</span>
            </v-card>
            <v-card class="pa-2 elevation-0" color="transparent">
              <span class="subtitle-1"> <v-icon class="mr-1">date_range</v-icon>UAT End: </span>
              <span class="subtitle-1" data-cy="uatDashboard__packStatus--uatEnd">{{
                formatUatDate(pack.uatEnd)
              }}</span>
            </v-card>
            <pack-timeline-dialog :record="pack"></pack-timeline-dialog>
          </v-row>
        </v-col>
      </v-row>

      <h2 class="title font-weight-thin mt-10 mb-0">Testing Status Counts</h2>
      <p class="caption grey--text text--darken-3 mb-0">
        Click a card to view a filtered list of release notes.
      </p>
      <v-row cols="12" class="mt-2">
        <v-col sm="12">
          <v-row>
            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToTotal()" @keydown.enter.prevent="navigateToTotal()" @keydown.space.prevent="navigateToTotal()">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">Total</div>
              <v-divider></v-divider>
              <div class="text-sm-center pt-2 display-1">
                <span class="primary--text" data-cy="uatDashboard__packTotals--total">{{ packTotals.total }}</span>
              </div>
            </v-card>

            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToNotStarted()" @keydown.enter.prevent="navigateToNotStarted()" @keydown.space.prevent="navigateToNotStarted()">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">Not Started</div>
              <v-divider></v-divider>
              <div class="text-sm-center pa-2 display-1">
                <span class="primary--text" data-cy="uatDashboard__packTotals--notStarted">{{
                  packTotals.notStarted
                }}</span>
              </div>
            </v-card>

            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToStatusFilter('In Process')" @keydown.enter.prevent="navigateToStatusFilter('In Process')" @keydown.space.prevent="navigateToStatusFilter('In Process')">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">In Process</div>
              <v-divider></v-divider>
              <div class="text-sm-center pa-2 display-1">
                <span class="primary--text" data-cy="uatDashboard__packTotals--inProcess">{{
                  packTotals.inProcess
                }}</span>
              </div>
            </v-card>

            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToStatusFilter('Passed')" @keydown.enter.prevent="navigateToStatusFilter('Passed')" @keydown.space.prevent="navigateToStatusFilter('Passed')">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">Passed</div>
              <v-divider></v-divider>
              <div class="text-sm-center pa-2 display-1">
                <span class="primary--text" data-cy="uatDashboard__packTotals--passed">{{ packTotals.passed }}</span>
              </div>
            </v-card>

            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToStatusFilter('Failed')" @keydown.enter.prevent="navigateToStatusFilter('Failed')" @keydown.space.prevent="navigateToStatusFilter('Failed')">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">Failed</div>
              <v-divider></v-divider>
              <div class="text-sm-center pa-2 display-1">
                <span class="primary--text" data-cy="uatDashboard__packTotals--failed">{{ packTotals.failed }}</span>
              </div>
            </v-card>

            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToStatusFilter('Implement Later')" @keydown.enter.prevent="navigateToStatusFilter('Implement Later')" @keydown.space.prevent="navigateToStatusFilter('Implement Later')">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">Implement Later</div>
              <v-divider></v-divider>
              <div class="text-sm-center pa-2 display-1">
                <span class="primary--text" data-cy="uatDashboard__packTotals--implementLater">{{
                  packTotals.implementLater
                }}</span>
              </div>
            </v-card>

            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToStatusFilter('Question')" @keydown.enter.prevent="navigateToStatusFilter('Question')" @keydown.space.prevent="navigateToStatusFilter('Question')">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">Question</div>
              <v-divider></v-divider>
              <div class="text-sm-center pa-2 display-1">
                <span class="primary--text" data-cy="uatDashboard__packTotals--question">{{
                  packTotals.question
                }}</span>
              </div>
            </v-card>

            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToStatusFilter('Reviewed')" @keydown.enter.prevent="navigateToStatusFilter('Reviewed')" @keydown.space.prevent="navigateToStatusFilter('Reviewed')">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">Reviewed</div>
              <v-divider></v-divider>
              <div class="text-sm-center pa-2 display-1">
                <span class="primary--text" data-cy="uatDashboard__packTotals--reviewed">{{
                  packTotals.reviewed
                }}</span>
              </div>
            </v-card>

            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToStatusFilter('Not Impacted')" @keydown.enter.prevent="navigateToStatusFilter('Not Impacted')" @keydown.space.prevent="navigateToStatusFilter('Not Impacted')">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">Not Impacted</div>
              <v-divider></v-divider>
              <div class="text-sm-center pa-2 display-1">
                <span class="primary--text" data-cy="uatDashboard__packTotals--notImpacted">{{
                  packTotals.notImpacted
                }}</span>
              </div>
            </v-card>

            <v-card height="100" width="146" class="mt-2 och-clickable" hover role="button" tabindex="0" @click="navigateToUnassigned()" @keydown.enter.prevent="navigateToUnassigned()" @keydown.space.prevent="navigateToUnassigned()">
              <div class="text-sm-center text-uppercase subtitle-2 pa-2">Unassigned</div>
              <v-divider></v-divider>
              <div class="text-sm-center pa-2 display-1">
                <span class="grey--text text--darken-3" data-cy="uatDashboard__packTotals--unassigned">{{
                  packTotals.unassigned
                }}</span>
              </div>
            </v-card>
          </v-row>
        </v-col>
      </v-row>

      <h2 class="title font-weight-thin mt-12 mb-0">Release Notes by Category</h2>
      <p class="caption grey--text text--darken-3 mb-0">
        Click a chart segment or legend item to view a filtered list of release notes.
      </p>
      <v-row wrap class="mt-2">
        <v-card class="mt-2">
          <div class="text-sm-center text-uppercase subtitle-2 pa-2">By Build Type</div>
          <v-divider></v-divider>
          <v-row class="pa-2">
            <v-flex class="och-doughnutChartContainer">
              <doughnut-chart
                :chart-data="buildTypeChartData"
                :options="doughnutOptsMap['buildType']"
                legendId="build-type-legend"
                data-cy="uatDashboard__chart--buildType"
              ></doughnut-chart>
            </v-flex>
            <v-flex align-self-center id="build-type-legend" class="och-legendContainer och-legendContainer--clickable" @click="onLegendClick($event, 'buildType')" @keydown.enter.prevent="onLegendClick($event, 'buildType')" @keydown.space.prevent="onLegendClick($event, 'buildType')"></v-flex>
          </v-row>
        </v-card>
        <v-card class="mt-2">
          <div class="text-sm-center text-uppercase subtitle-2 pa-2">By Release Type</div>
          <v-divider></v-divider>
          <v-row class="pa-2">
            <v-flex class="och-doughnutChartContainer">
              <doughnut-chart
                :chart-data="releaseTypeChartData"
                :options="doughnutOptsMap['releaseType']"
                legendId="release-type-legend"
                data-cy="uatDashboard__chart--releaseType"
              ></doughnut-chart>
            </v-flex>
            <v-flex align-self-center id="release-type-legend" class="och-legendContainer och-legendContainer--clickable" @click="onLegendClick($event, 'releaseType')" @keydown.enter.prevent="onLegendClick($event, 'releaseType')" @keydown.space.prevent="onLegendClick($event, 'releaseType')"></v-flex>
          </v-row>
        </v-card>
        <v-card class="mt-2">
          <div class="text-sm-center text-uppercase subtitle-2 pa-2">By User Role</div>
          <v-divider></v-divider>
          <v-row class="pa-2">
            <v-flex class="och-doughnutChartContainer">
              <doughnut-chart
                :chart-data="userRoleChartData"
                :options="doughnutOptsMap['userRole']"
                legendId="user-role-legend"
                data-cy="uatDashboard__chart--userRole"
              ></doughnut-chart>
            </v-flex>
            <v-flex align-self-center id="user-role-legend" class="och-legendContainer och-legendContainer--clickable" @click="onLegendClick($event, 'userRole')" @keydown.enter.prevent="onLegendClick($event, 'userRole')" @keydown.space.prevent="onLegendClick($event, 'userRole')"></v-flex>
          </v-row>
        </v-card>
        <v-card class="mt-2">
          <div class="text-sm-center text-uppercase subtitle-2 pa-2">By Application</div>
          <v-divider></v-divider>
          <v-row class="pa-2">
            <v-flex class="och-doughnutChartContainer">
              <doughnut-chart
                :chart-data="applicationChartData"
                :options="doughnutOptsMap['application']"
                legendId="module-legend"
                data-cy="uatDashboard__chart--module"
              ></doughnut-chart>
            </v-flex>
            <v-flex align-self-center id="module-legend" class="och-legendContainer och-legendContainer--clickable" @click="onLegendClick($event, 'application')" @keydown.enter.prevent="onLegendClick($event, 'application')" @keydown.space.prevent="onLegendClick($event, 'application')"></v-flex>
          </v-row>
        </v-card>
        <v-card class="mt-2">
          <div class="text-sm-center text-uppercase subtitle-2 pa-2">By Operationalize Type</div>
          <v-divider></v-divider>
          <v-row class="pa-2">
            <v-flex class="och-doughnutChartContainer">
              <doughnut-chart
                :chart-data="operationalizeTypeChartData"
                :options="doughnutOptsMap['operationalizeType']"
                legendId="operationalize-type-legend"
                data-cy="uatDashboard__chart--operationalizeType"
              ></doughnut-chart>
            </v-flex>
            <v-flex align-self-center id="operationalize-type-legend" class="och-legendContainer och-legendContainer--clickable" @click="onLegendClick($event, 'operationalizeType')" @keydown.enter.prevent="onLegendClick($event, 'operationalizeType')" @keydown.space.prevent="onLegendClick($event, 'operationalizeType')"></v-flex>
          </v-row>
        </v-card>
      </v-row>
      <v-flex class="mt-12">
        <h2 class="title font-weight-thin my-2">Release Notes by Assignee</h2>
        <p class="caption grey--text text--darken-3 mb-2">
          Click an assignee name to view a filtered list of their notes, or click a bar segment to also filter by status.
        </p>
      </v-flex>
      <v-card>
        <v-row wrap>
          <v-card
            v-for="item in statusByAssigneeChartData"
            :key="item.assignee"
            height="400"
            width="350"
            class="pa-2 mr-2 elevation-0"
          >
            <span class="text-sm-center text-uppercase subtitle-2 pa-2">Assignee:</span>
            <button
              type="button"
              class="och-assigneeLink primary--text"
              data-cy="uatDashboard__chartTitle--assignee"
              @click="navigateToAssignee(item.assignee)"
            >
              <v-icon class="mr-1" color="primary" small aria-hidden="true">open_in_new</v-icon>{{ relabelUnassigned(item.assignee) }}
            </button>
            <div class="subtitle-2 pa-2" data-cy="uatDashboard__chartTitle--totalAssigned">
              Total Assigned Notes: {{ item.totalAssignedNotes }}
            </div>
            <horizontal-bar-chart
              :chart-data="item.chartData"
              :options="barChartOptsMap[item.assignee]"
              data-cy="uatDashboard__chart--totalAssigned"
            ></horizontal-bar-chart>
          </v-card>
        </v-row>
      </v-card>

      <v-row class="mt-8" cols="12">
        <v-col sm="12">
          <h2 class="title font-weight-thin my-2">Member Organization Testing Status</h2>
          <v-card>
            <dashboard-testing-status-table class="pa-2"></dashboard-testing-status-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import DashboardTestingStatusTable from "@/components/dashboardTestingStatusTable";
import DoughnutChart from "@/components/doughnutChart";
import HorizontalBarChart from "@/components/horizontalBarChart";
import PackTimelineDialog from "@/components/PackTimelineDialog";
import ProgressBar from "@/components/progressBar";
import { formatDateAsMMDDYYYY } from "../lib/formatters";
import { mapActions } from "vuex";
import { isNavigationFailure, NavigationFailureType } from "vue-router";

function escapeHtml(value) {
  return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export default {
  name: "memberDashboard",
  components: {
    DashboardTestingStatusTable,
    DoughnutChart,
    HorizontalBarChart,
    PackTimelineDialog,
    ProgressBar,
  },
  computed: {
    releaseNoteByAssigneeChartOpts() {
      return {
        legend: {
          display: false,
        },
        labels: {
          width: 10,
        },
        scales: {
          xAxes: [
            {
              ticks: {
                beginAtZero: true,
                max: this.releaseNoteByAssigneeMaxSteps,
                stepSize: 5,
              },
            },
          ],
          yAxes: [
            {
              barThickness: 25,
            },
          ],
        },
      };
    },
    buildTypeChartData() {
      const data = this.$store.state.pack.packBreakdown.buildType;
      return this.fillChart(data);
    },
    barChartOptsMap() {
      return this.statusByAssigneeChartData.reduce((map, item) => {
        map[item.assignee] = this.makeBarChartOpts(item.assignee);
        return map;
      }, {});
    },
    doughnutOptsMap() {
      return ["buildType", "releaseType", "userRole", "application", "operationalizeType"].reduce((map, key) => {
        map[key] = this.makeDoughnutOpts(key);
        return map;
      }, {});
    },
    applicationNameToIdMap() {
      const list = this.$store.getters["admin/dropdown/getActiveApplicationList"] || [];
      return list.reduce((map, item) => {
        map[item.label] = item.id;
        return map;
      }, {});
    },
    doughnutChartOpts() {
      const options = {
        legendCallback(chart) {
          const legendHtml = [];
          legendHtml.push("<ul>");
          const item = chart.data.datasets[0];
          for (let i = 0; i < item.data.length; i += 1) {
            const safeLabel = escapeHtml(chart.data.labels[i]);
            legendHtml.push(`<li data-label="${safeLabel}">`);
            legendHtml.push(
              `<div class="och-legend__colorBlock" style="background-color: ${item.backgroundColor[i]}"></div>`
            );
            legendHtml.push(`<span class="och-legend__text"> ${escapeHtml(item.data[i])} - ${safeLabel} </span>`);
            legendHtml.push("</li>");
          }
          legendHtml.push("</ul>");
          return legendHtml.join("");
        },
        legend: {
          display: false,
        },
      };
      return options;
    },
    releaseNoteByAssigneeMaxSteps() {
      const data = this.$store.state.pack.statusByAssignee;
      let stepValue = 20;
      const result = Object.keys(data).map(prop => ({
        assignee: prop,
        releaseNoteStatus: data[prop],
        totalAssignedNotes: Object.values(data[prop]).reduce((a, b) => a + b),
      }));

      for (let assignee = 0; assignee < result.length; assignee += 1) {
        if (result[assignee].totalAssignedNotes > stepValue) {
          stepValue = result[assignee].totalAssignedNotes;
        }
      }

      // incrementing stepValue so the bars end in an increment of 5
      if (stepValue % 5 !== 0) {
        stepValue += 5 - (stepValue % 5);
      }

      return stepValue;
    },
    applicationChartData() {
      const data = this.$store.state.pack.packBreakdown.application;
      return this.fillChart(data);
    },
    operationalizeTypeChartData() {
      const data = this.$store.state.pack.packBreakdown.operationalizeType;
      return this.fillChart(data);
    },
    releaseTypeChartData() {
      const data = this.$store.state.pack.packBreakdown.releaseType;
      return this.fillChart(data);
    },
    userRoleChartData() {
      const data = this.$store.state.pack.packBreakdown.userRole;
      return this.fillChart(data);
    },
    packBreakdownByBuildType() {
      return this.$store.state.pack.packBreakdown.buildType;
    },
    packBreakdownByOperationalizeType() {
      return this.$store.state.pack.packBreakdown.operationalizeType;
    },
    packBreakdownByReleaseType() {
      return this.$store.state.pack.packBreakdown.releaseType;
    },
    packBreakdownByUserRole() {
      return this.$store.state.pack.packBreakdown.userRole;
    },
    pack() {
      return this.$store.state.pack.pack;
    },
    packTotals() {
      return this.$store.state.pack.packTotals;
    },
    statusByAssigneeChartData() {
      const data = this.$store.state.pack.statusByAssignee;
      const result = Object.keys(data).map(prop => ({
        assignee: prop,
        releaseNoteStatus: data[prop],
        totalAssignedNotes: Object.values(data[prop]).reduce((a, b) => a + b),
      }));

      result.forEach(item => {
        const chartData = this.fillBarChart(item.releaseNoteStatus);
        item.chartData = chartData;
      });

      return result;
    },
  },
  async created() {
    try {
      await Promise.all([this.getData(), this.fetchUserList()]);
    } catch (error) {
      console.error(error);
      this.$notifyError("Unable to fetch chart data");
    }
    try {
      await this.listApplications();
    } catch (error) {
      console.error(error);
    } finally {
      this.applicationsLoaded = true;
    }
  },
  data() {
    return {
      applicationsLoaded: false,
    };
  },
  methods: {
    fillBarChart(chartData) {
      if (!chartData) {
        return;
      }
      const dataVizPalette = [
        "rgb(64, 64, 64)", // gray = Not Started
        "rgb(28, 97, 196)", // blue = In Process
        "rgb(20, 150, 5)", //  green = Passed
        "rgb(255, 0, 0)", // red = Failed
        "rgb(255, 150, 0)", // orange = Question
        "rgb(255, 203, 5)", // yellow = Implement Later
        "rgb(204, 0, 153)", // magenta = Reviewed
        "rgb(125, 125, 125)", // medium/light gray = Not Impacted
      ];
      let chartConfig = {};
      let labels = [];
      let data = [];

      const formattedChartData = Object.entries(chartData);
      labels = formattedChartData.map(item => item[0]);
      data = formattedChartData.map(item => item[1]);

      chartConfig = {
        labels,
        datasets: [
          {
            label: null,
            backgroundColor: dataVizPalette,
            data,
          },
        ],
        borderWidth: 3,
      };
      return chartConfig;
    },
    fillChart(chartData) {
      if (!chartData) {
        return;
      }
      const dataVizPalette = [
        "rgb(28, 97, 196)", // blue
        "rgb(255, 102, 0)", // orange
        "rgb(255, 203, 5)", // yellow
        "rgb(20, 150, 5)", //  green
        "rgb(36, 173, 219)", // cyan
        "rgb(204, 0, 153)", // magenta
        "rgb(255, 0, 0)", // red
        "rgb(128, 0, 255)", // purple
        "rgb(64, 64, 64)", // gray
        "rgb(102, 140, 255)", // blue
        "rgb(255, 140, 102)", // orange
        "rgb(255, 255, 0)", // yellow
        "rgb(140, 255, 102)", // green
        "rgb(217, 102, 255)", // magenta
        "rgb(255, 102, 102)", // red
        "rgb(140, 102, 255)", // purple
        "rgb(204, 204, 204)", // gray
        "rgb(0, 0, 153)", // blue
        "rgb(255, 153, 0)", // orange
        "rgb(230, 204, 179)", // yellow
        "rgb(102, 153, 0)", // green
        "rgb(0, 153, 153)", // cyan
        "rgb(204, 0, 204)", // magenta
        "rgb(204, 0, 51)", // red
        "rgb(96, 0, 128)", // purple
        "rgb(50, 50, 50)", // gray
        "rgb(204, 230, 255)", // blue
        "rgb(204, 102, 0)", // orange
        "rgb(102, 102, 0)", // yellow
        "rgb(191, 255, 0)", // green
        "rgb(0, 102, 102)", // cyan
      ];
      let chartConfig = {};
      let labels = [];
      let data = [];

      const sortedChartData = Object.entries(chartData).sort((a, b) => {
        return b[1] - a[1];
      });

      labels = sortedChartData.map(item => item[0]);
      data = sortedChartData.map(item => item[1]);

      chartConfig = {
        labels,
        datasets: [
          {
            label: null,
            backgroundColor: dataVizPalette,
            data,
          },
        ],
        borderWidth: 3,
      };
      return chartConfig;
    },
    formatUatDate(dateStr) {
      return dateStr ? formatDateAsMMDDYYYY(dateStr) : "TBA";
    },
    async getData() {
      await Promise.all([this.getPackBreakdown(), this.getPack(), this.getPackTotals(), this.getStatusByAssignee()]);
    },
    async getPackBreakdown() {
      await this.$store.dispatch("pack/getPackBreakdown", {
        packId: this.selectedPackId,
        serviceAreaId: this.serviceArea,
      });
    },
    async getPack() {
      if (this.selectedPackId) {
        await this.$store.dispatch("pack/getPack", {
          id: this.selectedPackId,
        });
      }
    },
    async getPackTotals() {
      await this.$store.dispatch("pack/getPackTotals", {
        packId: this.selectedPackId,
        serviceAreaId: this.serviceArea,
      });
    },
    async getStatusByAssignee() {
      await this.$store.dispatch("pack/getStatusByAssignee", {
        packId: this.selectedPackId,
        serviceAreaId: this.serviceArea,
      });
    },
    makeBarChartOpts(assignee) {
      const base = this.releaseNoteByAssigneeChartOpts;
      return {
        ...base,
        onClick: (event, elements) => {
          if (!elements.length) return;
          const index = elements[0]._index;
          const status = elements[0]._chart.data.labels[index];
          this.navigateToAssigneeStatus(assignee, status);
        },
        onHover: (event, elements) => {
          event.target.style.cursor = elements.length ? "pointer" : "default";
        },
      };
    },
    makeDoughnutOpts(chartKey) {
      const base = this.doughnutChartOpts;
      return {
        ...base,
        legendCallback(chart) {
          const legendHtml = [];
          legendHtml.push("<ul>");
          const item = chart.data.datasets[0];
          for (let i = 0; i < item.data.length; i += 1) {
            const safeLabel = escapeHtml(chart.data.labels[i]);
            legendHtml.push(`<li data-label="${safeLabel}" tabindex="0" role="button">`);
            legendHtml.push(
              `<div class="och-legend__colorBlock" style="background-color: ${item.backgroundColor[i]}"></div>`
            );
            legendHtml.push(`<span class="och-legend__text"> ${escapeHtml(item.data[i])} - ${safeLabel} </span>`);
            legendHtml.push("</li>");
          }
          legendHtml.push("</ul>");
          return legendHtml.join("");
        },
        onClick: (event, elements) => {
          if (!elements.length) return;
          const index = elements[0]._index;
          const displayLabel = elements[0]._chart.data.labels[index];
          const resolved = this.resolveFilterValue(chartKey, displayLabel);
          if (!resolved) return;
          this.navigateToDrillThrough(chartKey, resolved.value, resolved.displayLabel);
        },
        onHover: (event, elements) => {
          event.target.style.cursor = elements.length ? "pointer" : "default";
        },
      };
    },
    resolveFilterValue(chartKey, displayLabel) {
      if (chartKey === "application") {
        if (!this.applicationsLoaded) return null;
        const id = this.applicationNameToIdMap[displayLabel];
        if (id == null) return null;
        return { value: id, displayLabel };
      }
      return { value: displayLabel, displayLabel };
    },
    pushToReleaseNoteList() {
      this.$router.push({
        name: "releaseNoteList",
        params: { packId: this.selectedPackId },
      }).catch((error) => {
        if (isNavigationFailure(error, NavigationFailureType.duplicated)) return;
        if (isNavigationFailure(error, NavigationFailureType.cancelled)) return;
        if (isNavigationFailure(error, NavigationFailureType.redirected)) return;
        this.$store.commit("pack/clearPendingDrillThrough");
        console.error(error);
        this.$notifyError({ message: "Could not navigate to release note list. Please try again." });
      });
    },
    navigateToDrillThrough(chartKey, filterValue, displayLabel, excludeNonQualifying = false) {
      this.$store.commit("pack/setPendingDrillThrough", {
        dashboardFilterField: chartKey,
        dashboardFilterValue: filterValue,
        dashboardFilterValueLabel: displayLabel,
        dashboardExcludeNonQualifying: excludeNonQualifying,
      });
      this.pushToReleaseNoteList();
    },
    navigateToStatusFilter(statusValue) {
      this.navigateToDrillThrough("status", statusValue, statusValue);
    },
    navigateToNotStarted() {
      this.navigateToDrillThrough("status", "Not Started", "Not Started", true);
    },
    navigateToUnassigned() {
      this.navigateToDrillThrough("assignedTo", null, "Unassigned", true);
    },
    navigateToTotal() {
      this.$store.commit("pack/setPendingDrillThrough", {});
      this.pushToReleaseNoteList();
    },
    navigateToAssignee(assignee) {
      const assigneeValue = assignee === "null" ? null : assignee;
      const displayLabel = this.relabelUnassigned(assignee);
      this.navigateToDrillThrough("assignedTo", assigneeValue, displayLabel);
    },
    navigateToAssigneeStatus(assignee, status) {
      const assigneeValue = assignee === "null" ? null : assignee;
      const displayLabel = this.relabelUnassigned(assignee);
      this.$store.commit("pack/setPendingDrillThrough", {
        dashboardFilterField: "assignedTo",
        dashboardFilterValue: assigneeValue,
        dashboardFilterValueLabel: displayLabel,
        dashboardFilterField2: "status",
        dashboardFilterValue2: status,
        dashboardFilterValueLabel2: status,
      });
      this.pushToReleaseNoteList();
    },
    onLegendClick(event, chartKey) {
      const li = event.target.closest("li[data-label]");
      if (!li) return;
      const resolved = this.resolveFilterValue(chartKey, li.dataset.label);
      if (!resolved) return;
      this.navigateToDrillThrough(chartKey, resolved.value, resolved.displayLabel);
    },
    relabelUnassigned(val) {
      if (!val || val === "null") {
        return "Unassigned";
      }

      const userMap = this.$store.getters["user/getUserMap"];
      const user = userMap[val];
      if (user) {
        return `${user.firstName} ${user.lastName}`;
      }

      return val;
    },
    ...mapActions({
      fetchUserList: "user/load",
      listApplications: "admin/dropdown/listApplications",
    }),
  },
  props: ["selectedPackId", "serviceArea"],
  watch: {
    $route() {
      try {
        this.getData();
      } catch (error) {
        console.error(error);
        this.$notifyError("Unable to fetch chart data");
      }
    },
  },
};
</script>

<style>
.och-doughnutChartContainer {
  height: 240px;
  padding: 0.75rem;
  width: 240px;
}

.och-doughnutChartLegend {
  max-width: 500px;
  padding-left: 0.75rem;
}

.och-legendContainer {
  padding: 0.5rem;
  width: 260px;
}

.och-legendContainer ul {
  list-style: none;
}

.och-legendContainer li {
  align-items: center;
  display: flex;
  margin-bottom: 0.5rem;
}

.och-legend__colorBlock {
  height: 1rem;
  margin-right: 0.5rem;
  width: 1rem;
}

.och-legend__text {
  font-size: 0.8rem;
}

.och-legendContainer--clickable li {
  cursor: pointer;
}

.och-legendContainer--clickable li:hover .och-legend__text {
  text-decoration: underline;
}

.och-clickable {
  cursor: pointer;
}

.och-clickable:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.och-assigneeLink {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
}

.och-assigneeLink:hover {
  text-decoration: underline;
}
</style>
